import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Table, Button, Select, InputNumber, Input, Space, Popconfirm } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Bonification } from '../Types/BonificationType'
import { Certification } from '../Types/CertificationType'

export type BonificationInput = Omit<Bonification, 'id' | 'created_at' | 'updated_at'> & {
  id?: number
  axis_capacity?: number
  suspension_capacity?: number
}

type Props = {
  value?: BonificationInput[]
  onChange?: (rows: BonificationInput[]) => void
  certification: Certification
}

const EMPTY_ROW = (cert: Certification, type: 'front' | 'rear' = 'rear'): BonificationInput => ({
  certification_id: cert.id ?? 0,
  type,
  suspension_type: 'MECANICO',
  axis_capacity: undefined,
  suspension_capacity: undefined,
  tire_capacity_a: '',
  tire_capacity_b: '',
  tire_capacity_c: '',
  tire_class: 'CONVENCIONAL',
})

const initialRows = (cert: Certification, isO4: boolean): BonificationInput[] => {
  if (isO4) {
    return [
      { ...EMPTY_ROW(cert, 'rear'), id: -1 },
      { ...EMPTY_ROW(cert, 'rear'), id: -2 },
      { ...EMPTY_ROW(cert, 'rear'), id: -3 },
    ]
  }
  return [
    { ...EMPTY_ROW(cert, 'front'), id: -1 },
    { ...EMPTY_ROW(cert, 'rear'), id: -2 },
    { ...EMPTY_ROW(cert, 'rear'), id: -3 },
    { ...EMPTY_ROW(cert, 'rear'), id: -4 },
  ]
}

export const BonificationGenerator: React.FC<Props> = ({
  value,
  onChange,
  certification,
}) => {
  const isO4 = (certification?.class || '').toUpperCase() === 'O4'
  const idSeq = useRef(1_000_000)

  const [rows, setRows] = useState<BonificationInput[]>(
    () => initialRows(certification, isO4)
  )

  const pushChange = (next: BonificationInput[]) => {
    setRows(next)
    onChange?.(next)
  }

  const seedDefaults = (notifyParent = false) => {
    const def = initialRows(certification, isO4)
    setRows(def)
    if (notifyParent) onChange?.(def)
  }

  // value: undefined -> no tocar; [] -> defaults; con items -> copiar/filtrar (en O4 quita 'front')
  useEffect(() => {
    if (value === undefined) return

    if (Array.isArray(value) && value.length === 0) {
      seedDefaults(true)
      return
    }

    const withIds = (value ?? []).map((r, i) => ({ ...r, id: r.id ?? i + 1 }))
    const sanitized = isO4 ? withIds.filter(r => r.type !== 'front') : withIds
    setRows(sanitized)
  }, [value, isO4, certification])

  // Si cambia la clase y es no controlado, resiembra defaults
  useEffect(() => {
    if (value === undefined) {
      seedDefaults(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certification?.class])

  const updateCell = <K extends keyof BonificationInput>(
    key: K,
    rowId: number | undefined,
    v: BonificationInput[K]
  ) => {
    const fixed = isO4 && key === 'type' && v === ('front' as any) ? ('rear' as any) : v
    const next = rows.map(r => (r.id === rowId ? { ...r, [key]: fixed } : r))
    pushChange(next)
  }

  const addRow = (type: 'front' | 'rear' = 'rear') => {
    const realType: 'front' | 'rear' = isO4 ? 'rear' : type
    const newRow: BonificationInput = { ...EMPTY_ROW(certification, realType), id: -(idSeq.current++) }
    pushChange([...rows, newRow])
  }

  const removeRow = (rowId?: number) => pushChange(rows.filter(r => r.id !== rowId))

  const columns: ColumnsType<BonificationInput> = useMemo(
    () => [
      {
        title: '#',
        key: 'idx',
        width: 48,
        fixed: 'left',
        align: 'center',
        render: (_: any, __: BonificationInput, index: number) => index + 1,
      },
      {
        title: 'Tipo',
        dataIndex: 'type',
        width: 110,
        render: (_: any, record) => (
          <Select
            value={record.type}
            options={
              isO4
                ? [{ value: 'rear', label: 'Trasero' }]
                : [
                    { value: 'front', label: 'Delantero' },
                    { value: 'rear', label: 'Trasero' },
                  ]
            }
            onChange={v => updateCell('type', record.id, v)}
            style={{ width: '100%' }}
            size="small"
          />
        ),
      },
      {
        title: 'Suspensión',
        dataIndex: 'suspension_type',
        width: 140,
        render: (_: any, record) => (
          <Select
            value={record.suspension_type}
            options={[
              { value: 'MECANICO', label: 'MECÁNICO' },
              { value: 'NEUMATICO', label: 'NEUMÁTICO' },
            ]}
            onChange={v => updateCell('suspension_type', record.id, v)}
            style={{ width: '100%' }}
            size="small"
          />
        ),
      },
      {
        title: 'Cap. eje',
        dataIndex: 'axis_capacity',
        width: 110,
        render: (_: any, record) => (
          <InputNumber
            value={record.axis_capacity}
            min={0}
            precision={2}
            onChange={v => updateCell('axis_capacity', record.id, (v ?? undefined) as number | undefined)}
            style={{ width: '100%' }}
            size="small"
          />
        ),
      },
      {
        title: 'Cap. suspensión',
        dataIndex: 'suspension_capacity',
        width: 130,
        render: (_: any, record) => (
          <InputNumber
            value={record.suspension_capacity}
            min={0}
            precision={2}
            onChange={v => updateCell('suspension_capacity', record.id, (v ?? undefined) as number | undefined)}
            style={{ width: '100%' }}
            size="small"
          />
        ),
      },
      {
        title: 'Aro',
        dataIndex: 'tire_capacity_a',
        width: 90,
        render: (_: any, record) => (
          <Input
            value={record.tire_capacity_a}
            onChange={e => updateCell('tire_capacity_a', record.id, e.target.value)}
            size="small"
          />
        ),
      },
      {
        title: 'Medida neum.',
        dataIndex: 'tire_capacity_b',
        width: 140,
        render: (_: any, record) => (
          <Input
            value={record.tire_capacity_b}
            onChange={e => updateCell('tire_capacity_b', record.id, e.target.value)}
            size="small"
          />
        ),
      },
      {
        title: 'Cap. neum. (Kg)',
        dataIndex: 'tire_capacity_c',
        width: 140,
        render: (_: any, record) => (
          <Input
            value={record.tire_capacity_c}
            onChange={e => updateCell('tire_capacity_c', record.id, e.target.value)}
            size="small"
          />
        ),
      },
      {
        title: 'Clase neumático',
        dataIndex: 'tire_class',
        width: 150,
        render: (_: any, record) => (
          <Select
            value={record.tire_class}
            options={[
              { value: 'CONVENCIONAL', label: 'CONVENCIONAL' },
              { value: 'EXTRA ANCHO', label: 'EXTRA ANCHO' },
            ]}
            onChange={v => updateCell('tire_class', record.id, v)}
            style={{ width: '100%' }}
            size="small"
          />
        ),
      },
      {
        title: 'Acciones',
        key: 'actions',
        fixed: 'right',
        width: 90,
        render: (_: any, record) => (
          <Popconfirm title="Eliminar fila" onConfirm={() => removeRow(record.id)}>
            <Button danger size="small">Eliminar</Button>
          </Popconfirm>
        ),
      },
    ],
    [rows, isO4]
  )

  return (
    <Space className="bonification-generator" direction="vertical" style={{ width: '100%' }}>
      <Table<BonificationInput>
        rowKey={r => String(r.id)}
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 1000 }}
        size="small"
      />
      <div className="bonification-actions">
        {!isO4 && (
          <Button onClick={() => addRow('front')} size="small">
            Agregar delantero
          </Button>
        )}
        <Button type="dashed" onClick={() => addRow('rear')} size="small">
          Agregar trasero
        </Button>
      </div>
    </Space>
  )
}
